/**
 * Digital Data Manager
 * ---------------------------------------------------------------------------
 * A lightweight, site-agnostic data layer helper that standardizes how
 * analytics/experience platforms read/write state to `window.digitalData`.
 *
 * Structure:
 *   window.digitalDataManager
 *     - utilities: pure helpers (cookies, URL params, value extraction, sessions)
 *     - init(): bootstraps base objects and page state
 *     - setters/getters for cart, order, products, search, subscription, forms, widgets
 *     - fishersci: Fisher Scientific-specific page logic (names/types/meta/store/user/breadcrumbs)
 *
 * Notes:
 * - This file intentionally performs no network I/O or vendor SDK calls.
 * - Consumers should read/write via these functions to keep the data layer consistent.
 * - All functions are side-effectful only to `window.digitalData` and local/session storage.
 */

/* global window document localStorage sessionStorage */

window.digitalDataManager = {
    timestamp: "2025-11-14T14:19:59.273Z",
    /**
     * Version
     */
    version: function (){
        return '37.0.0';
    },
    /**
     * -------------------------------------------------------------------------
     * Utilities
     * Helpers for cookie access, URL query parsing, deep value extraction, etc.
     * -------------------------------------------------------------------------
     */
    utilities: {

        /**
         * Read a cookie by name.
         * @param {string} name - Cookie name.
         * @returns {string} Decoded cookie value or empty string if not found.
         */
        getCookie: function (name) {
            var nameEq = name + "=";
            var parts = String(document.cookie || "").split(";");
            for (var i = 0; i < parts.length; i++) {
                var c = parts[i];
                while (c.charAt(0) === " ") { c = c.substring(1); }
                if (c.indexOf(nameEq) === 0) {
                    var v = c.substring(nameEq.length);
                    try { return decodeURIComponent(v); } catch (e) { return v; }
                }
            }
            return "";
        },

        /**
         * Parse a URL query string (e.g., `location.search`) into a key/value object.
         * Decodes keys and values; missing values default to an empty string.
         * @param {string} search - A query string starting with "?" or raw "a=b&c=d".
         * @returns {Object<string,string>} Decoded map of query parameters.
         */
        extractURLParams: function (search) {
            if (!search) { return {}; }
            var s = search.charAt(0) === "?" ? search.substring(1) : search;
            var out = {};
            var parts = s.split("&");
            for (var i = 0; i < parts.length; i++) {
                if (!parts[i]) { continue; }
                var kv = parts[i].split("=");
                var k = kv[0], v = kv.length > 1 ? kv[1] : "";
                try { k = decodeURIComponent(k); } catch (e) {}
                try { v = decodeURIComponent(v); } catch (e) {}
                out[k] = v;
            }
            return out;
        },

        /**
         * Safely extract a nested property from an object using a dot-path.
         * Avoids repeated `if` checks and returns `undefined` when any segment is missing.
         * @example
         *   extractValue(digitalData, "general.country")
         * @param {Object} object - Source object.
         * @param {string} variable - Dot-delimited path (e.g., "a.b.c").
         * @returns {*} The value at the path or `undefined`.
         */
        extractValue: function (object, variable) {
            if (typeof object !== "undefined") {
                var keys = variable.split(".");
                if (keys.length > 1 && Object.prototype.hasOwnProperty.call(object, keys[0])) {
                    return window.digitalDataManager.utilities.extractValue(
                        object[keys[0]],
                        keys.splice(1).join(".")
                    );
                } else if (Object.prototype.hasOwnProperty.call(object, keys[0])) {
                    return object[keys[0]];
                } else {
                    return undefined;
                }
            }
        },

        /**
         * Generate a pseudo-unique session value for forms/widgets.
         * Combines current timestamp and a base36 random suffix.
         * @returns {string} Session identifier (e.g., "1700000000000_k9d3f0p2q").
         */
        generateSessionValue: function() {
            var timestamp = Date.now();
            var randomID = Math.random().toString(36).substr(2,9);
            return `${timestamp}_${randomID}`;
        }
    },

    /**
     * Initialize the base dataLayer and page object.
     * Sets `general`, and derives the initial `page` info.
     *
     * @param {string} isoCode - Lowercase two-letter country code (e.g., "us").
     * @param {string} lang - Language code (e.g., "en").
     * @param {string} currencyCode - ISO 4217 three-letter currency code (e.g., "USD").
     * @param {string} currencySymbol - Display currency symbol (e.g., "$").
     */
    init: function(isoCode, lang, currencyCode, currencySymbol) {
        window.digitalData = window.digitalData || {};
        window.digitalDataManager.setGeneral(isoCode, lang, currencyCode, currencySymbol);
        window.digitalDataManager.setPage();
    },

    /**
     * ----------------------------------------------------------------------------
     * CART HELPERS
     * ----------------------------------------------------------------------------
     */

    /**
     * Retrieve the product finding method for a line item in a given cart
     * from persisted localStorage state.
     * @param {string} cartId - Cart identifier.
     * @param {string|number} productId - Line item identifier.
     * @returns {string|undefined} Finding method or `undefined` if unknown.
     */
    getCartItemFindingMethod: function (cartId, productId) {
        if (localStorage.hasOwnProperty("cartFindingMethod")) {
            var findingMethods = JSON.parse(localStorage.cartFindingMethod);
            return findingMethods[cartId] ? findingMethods[cartId][productId] : undefined;
        }
        return undefined;
    },

    /**
     * Persist/overwrite the product finding method for the current cart’s line item.
     * Uses `window.digitalData.cart.id` as the cart key.
     * @param {string|number} productId - Line item identifier.
     * @param {string} findingMethod - Source of add-to-cart (e.g., "PDP", "PLP", "Search").
     */
    persistCartItemFindingMethod: function (productId, findingMethod) {
        if (window.digitalData.cart) {
            var cartFindingMethod = {};

            if (localStorage.hasOwnProperty("cartFindingMethod")) {
                cartFindingMethod = JSON.parse(localStorage.cartFindingMethod);
            }

            if (!cartFindingMethod[window.digitalData.cart.id]) {
                cartFindingMethod[window.digitalData.cart.id] = {};
            }

            cartFindingMethod[window.digitalData.cart.id][productId] = findingMethod;
            localStorage.cartFindingMethod = JSON.stringify(cartFindingMethod);
        }
    },

    /**
     * Define/update the cart summary object (totals and cost breakdown).
     * @param {string} id - Current cart id.
     * @param {number} subtotal - Cart subtotal from backend.
     * @param {number} [total=0] - Cart grand total (optional).
     * @param {Object<string,number>} [costBreakdown={}] - Additional keyed charges
     *   (e.g., { tax: 1.23, shipping: 4.56, ice: 0.00 }).
     */
    setCart: function(id, subtotal, total = 0, costBreakdown = {}) {
        window.digitalData.cart = window.digitalData.cart || {};
        window.digitalData.cart.id = id;
        window.digitalData.cart.subtotal = subtotal;
        if (total) {
            window.digitalData.cart.total = total;
        }
        window.digitalData.cart = Object.assign(window.digitalData.cart, costBreakdown);
    },

    /**
     * Add/update a product line item in the cart.
     * Stores/derives the finding method and persists it to localStorage.
     * @param {string|number} lineItemId - Line item id.
     * @param {string} sku - Product SKU/ID.
     * @param {string} variation - UOM/variation (e.g., "case of 12").
     * @param {string} name - Product display name/title.
     * @param {number} price - Unit price (float).
     * @param {number} quantity - Quantity added.
     * @param {string} [findingMethod] - If not passed, will attempt to read prior value.
     */
    setCartItem: function(lineItemId, sku, variation, name, price, quantity, findingMethod) {
        if (!findingMethod) {
            findingMethod = window.digitalDataManager.getCartItemFindingMethod(
                window.digitalData.cart.id,
                lineItemId
            );
        }
        window.digitalDataManager.setItem(
            window.digitalData.cart,
            lineItemId, sku, variation, name, price, quantity, findingMethod
        );
        window.digitalDataManager.persistCartItemFindingMethod(lineItemId, findingMethod);
    },

    /**
     * Remove a single line item from the cart.
     * @param {string|number} lineItemId - Line item id to delete.
     */
    removeCartItem: function (lineItemId) {
        delete window.digitalData.cart.products[lineItemId];
    },

    /**
     * Remove all line items from the cart.
     * Deletes the `products` object under `cart`.
     */
    removeCartItems: function () {
        delete window.digitalData.cart.products;
    },

    /**
     * ----------------------------------------------------------------------------
     * GENERAL / PAGE
     * ----------------------------------------------------------------------------
     */

    /**
     * Set site-wide general context (country/language/currency).
     * @param {string} isoCode - Lowercase two-letter country code (e.g., "us").
     * @param {string} lang - Language code (e.g., "en").
     * @param {string} currencyCode - ISO 4217 code (e.g., "USD").
     * @param {string} currencySymbol - Display symbol (e.g., "$").
     */
    setGeneral: function(isoCode, lang, currencyCode, currencySymbol) {
        window.digitalData.general = {
            country: window.digitalDataManager.utilities.countries[isoCode],
            language: lang,
            currencyCode: currencyCode,
            currencySymbol: currencySymbol,
        }
    },

    /**
     * Internal utility to set a product object on a given parent (cart or order).
     * Prefer using `setCartItem` or `setOrderItem`.
     * @param {Object} parent - Reference to `digitalData.cart` or `digitalData.order`.
     * @param {string|number} lineItemId - Line item id.
     * @param {string} sku - Product SKU/ID.
     * @param {string} variation - UOM/variation label.
     * @param {string} name - Product display name/title.
     * @param {number} price - Unit price (float).
     * @param {number} quantity - Quantity selected.
     * @param {string} findingMethod - How the product was added (e.g., "PDP").
     */
    setItem: function(parent, lineItemId, sku, variation, name, price, quantity, findingMethod) {
        parent.products = parent.products || {};
        parent.products[lineItemId] = {
            sku: sku,
            name: name,
            variation: variation,
            price: price,
            quantity: quantity,
            findingMethod: findingMethod
        };
    },

    /**
     * Add/update a product line item in the order object.
     * Mirrors cart items and retains the finding method when available.
     * @param {string|number} lineItemId
     * @param {string} sku
     * @param {string} variation
     * @param {string} name
     * @param {number} price
     * @param {number} quantity
     * @param {string} [findingMethod]
     */
    setOrderItem: function (lineItemId, sku, variation, name, price, quantity, findingMethod) {
        if (!findingMethod) {
            findingMethod = window.digitalDataManager.getCartItemFindingMethod(
                window.digitalData.cart.id,
                lineItemId
            );
        }
        window.digitalDataManager.setItem(
            window.digitalData.order,
            lineItemId, sku, variation, name, price, quantity, findingMethod
        );
    },

    /**
     * Define/update the order summary object.
     * @param {string} id - Order id.
     * @param {number} subtotal - Order subtotal.
     * @param {Object<string,number>} [costBreakdown={}] - Additional keyed charges.
     * @param {string} [promoCode=""] - Applied promo code if any.
     * @param {string} [paymentMethod=""] - Payment method label.
     * @param {string} [status=""] - Order status string.
     */
    setOrder: function(id, subtotal, costBreakdown = {}, promoCode = "", paymentMethod = "", status = "") {
        window.digitalData.order = window.digitalData.order || {};
        window.digitalData.order.id = id;
        window.digitalData.order.subtotal = subtotal;
        window.digitalData.order = Object.assign(window.digitalData.order, costBreakdown);
        window.digitalData.order.promoCode = promoCode;
        window.digitalData.order.paymentMethod = paymentMethod;
        window.digitalData.order.status = status;
    },

    /**
     * Attach funding details to the current order.
     * @param {string} id - Funding id.
     * @param {string} name - Funding source name.
     * @param {number} balance - Remaining balance.
     */
    setFunding: function (id, name, balance) {
        window.digitalData.order.funding = { id: id, name: name, balance: balance };
    },

    /**
     * Initialize or refresh the page object (URL, params, name, type).
     * Auto-derives `params` from `location.search`, `name` and `type` via site logic.
     */
    setPage: function () {
        window.digitalData.page = {
            url: document.location.href,
            params: window.digitalDataManager.utilities.extractURLParams(document.location.search),
            name: window.digitalDataManager.setPageName(),
            type: window.digitalDataManager.setPageType()
        };
        if (!Object.keys(window.digitalData.page.params).length) {
            delete window.digitalData.page.params;
        }
    },

    /**
     * Explicitly set or derive the page name.
     * If `name` is omitted, delegates to site logic (Fishersci).
     * @param {string} [name=""] - Page name override.
     * @returns {string|undefined} The derived name when not setting directly.
     */
    setPageName: function (name = "") {
        if (name) {
            window.digitalData.page.name = name;
        } else {
            return window.digitalDataManager.fishersci.setPageName();
        }
    },

    /**
     * Explicitly set or derive the page type.
     * If `type` is omitted, delegates to site logic (Fishersci).
     * @param {string} [type=""] - Page type override.
     * @returns {string|undefined} The derived type when not setting directly.
     */
    setPageType: function (type = "") {
        if (type) {
            window.digitalData.page.type = type;
        } else {
            return window.digitalDataManager.fishersci.setPageType();
        }
    },

    /**
     * ----------------------------------------------------------------------------
     * PRODUCTS
     * ----------------------------------------------------------------------------
     */

    /**
     * Set a group of products under a labeled type bucket.
     * - For type "primary", replaces the entire object for that type.
     * - For all other types, merges into existing products for that type (last write wins).
     * @param {string} type - Product group key (e.g., "primary", "recommended").
     * @param {Object<string,Object>} products - Map keyed by SKU with product objects.
     */
    setProduct: function(type, products) {
        window.digitalData.products = window.digitalData.products || {};
        window.digitalData.products[type] = window.digitalData.products[type] || {};
        if (type == "primary") {
            window.digitalData.products[type] = products;
        } else {
            window.digitalData.products[type] = Object.assign(products, window.digitalData.products[type]);
        }
    },

    /**
     * Update availability info for a product post-render.
     * @param {string} type - Product group key (e.g., "primary", "recommended").
     * @param {string} sku - SKU key within the group map.
     * @param {Object} availability - Availability payload (shape defined by your backend).
     */
    updateProductAvailability: function (type, sku, availability) {
        if (window.digitalData.products[type][sku]) {
            window.digitalData.products[type][sku].availability = availability;
        }
    },

    /**
     * Update rating info for a product post-render.
     * @param {string} type - Product group key.
     * @param {string} sku - SKU key within the group map.
     * @param {number|Object} rating - Rating payload (number or detailed object).
     */
    updateProductRating: function (type, sku, rating) {
        if (window.digitalData.products[type][sku]) {
            window.digitalData.products[type][sku].rating = rating;
        }
    },

    /**
     * ----------------------------------------------------------------------------
     * SEARCH
     * ----------------------------------------------------------------------------
     */

    /**
     * Record search context (term, type, filters, pagination).
     * @param {string} term - Raw user query.
     * @param {string} type - Search surface (e.g., "products", "certificates", "sds").
     * @param {string} experienceType - Experience variant (e.g., "rapid", "standard").
     * @param {Object<string, (string|number|boolean)>} [filters={}] - Applied filters as K/V pairs.
     * @param {number} [page=1] - Current results page (1-based).
     * @param {number} totalResults - Total number of results.
     */
    setSearch: function (term, type, experienceType, filters = {}, page = 1, totalResults) {
        window.digitalData.search = window.digitalData.search || {};
        window.digitalData.search.term = term;
        window.digitalData.search.type = type;
        window.digitalData.search.experienceType = experienceType;
        if (filters) {
            window.digitalData.search.filters = filters;
        }
        window.digitalData.search.page = page;
        window.digitalData.search.totalResults = totalResults;
    },

    /**
     * ----------------------------------------------------------------------------
     * SUBSCRIPTION
     * ----------------------------------------------------------------------------
     */

    /**
     * Record subscription details (e.g., created on order confirmation).
     * If no id is provided, uses sessionStorage "subscriptionId"; otherwise generates one.
     * @param {string} [id=sessionStorage.getItem("subscriptionId")] - Subscription id.
     * @param {Object<string,Object>} [products={}] - Products map, similar to cart format.
     * @param {string} schedule - "start date | frequency | end date" OR "Custom".
     * @param {string} paymentMethod - "Account" or "Credit Card".
     */
    setSubscription: function (id = sessionStorage.getItem("subscriptionId"), products = {}, schedule, paymentMethod) {
        window.digitalData.subscription = window.digitalData.subscription || {};
        if (id) {
            window.digitalData.subscription.id = id;
        } else {
            window.digitalData.subscription.id = Date.now();
            sessionStorage.setItem("subscriptionId", window.digitalData.subscription.id);
        }
        window.digitalData.subscription.products = products;
        window.digitalData.subscription.schedule = schedule;
        window.digitalData.subscription.paymentMethod = paymentMethod;
    },

    /**
     * ----------------------------------------------------------------------------
     * FORMS & WIDGETS
     * ----------------------------------------------------------------------------
     */

    /**
     * Initialize a form entry under `digitalData.forms`.
     * Auto-generates a sessionId for tracking a form session.
     * @param {string} formId - Unique identifier for the form (avoid special chars).
     */
    setForm: function (formId) {
        window.digitalData.forms = window.digitalData.forms || {};
        window.digitalData.forms[formId] = {
            id: formId,
            sessionId: window.digitalDataManager.utilities.generateSessionValue()
        };
    },

    /**
     * Record an offer impression/availability in the data layer.
     * Pushes one offer at a time; repeated calls append.
     * @param {string} placement - Placement identifier (e.g., "pdp_banner").
     * @param {string} name - Offer name.
     * @param {string} [source=""] - Attribution source (optional).
     * @param {string} [code=""] - Unique offer code (optional).
     * @param {string} [icid=""] - Internal campaign id (optional).
     */
    setOffer: function (placement, name, source = "", code = "", icid = "") {
        window.digitalData.offers = window.digitalData.offers || [];
        window.digitalData.offers.push({ placement: placement, name: name, source: source, code: code, icid: icid });
    },

    /**
     * Initialize a multi-step wizard widget container.
     * @param {string} name - Wizard name.
     * @param {number} [totalSteps=1] - Total number of steps.
     */
    setWizard: function (name, totalSteps = 1) {
        window.digitalData.widgets = window.digitalData.widgets || {};
        window.digitalData.widgets.wizard = {
            name: name,
            currentResponse: {},
            responses: {},
            totalSteps: totalSteps
        };
    },

    /**
     * Record the current step response and store it in the cumulative responses map.
     * @param {number} number - Step number (1-based).
     * @param {string} response - User-friendly response text/value.
     */
    recordWizardResponse: function (number, response) {
        window.digitalData.widgets.wizard.currentResponse = {
            number: number,
            response: response
        };
        window.digitalData.widgets.wizard.responses[number] = response;
    },

    /**
     * ----------------------------------------------------------------------------
     * Fisher Scientific (site-specific logic)
     * ----------------------------------------------------------------------------
     */
    fishersci: {
        /**
         * Site-specific bootstrap: general init + store, page meta, breadcrumbs, user.
         * @param {string} isoCode
         * @param {string} lang
         * @param {string} currencyCode
         * @param {string} currencySymbol
         */
        init: function (isoCode, lang, currencyCode, currencySymbol) {
            window.digitalDataManager.init(isoCode, lang, currencyCode, currencySymbol);
            window.digitalDataManager.fishersci.setStore();
            window.digitalDataManager.fishersci.setPageMeta();
            window.digitalData.widgets = window.digitalData.widgets || {};
            var breadcrumbs = window.digitalDataManager.fishersci.setBreadcrumbs();
            if (breadcrumbs.length > 0) {
                window.digitalData.page.breadcrumbs = breadcrumbs;
            }
            if (sessionStorage.fisherUserDetails) {
                var userDetail = JSON.parse(sessionStorage.fisherUserDetails);
                digitalDataManager.fishersci.setUser(userDetail);
            }
        },

        /**
         * Helper for SPA route changes: refresh derived page attributes.
         * Updates `page.name` and `page.url` only.
         */
        spaPageUpdate: function () {
            window.digitalData.page.name = window.digitalDataManager.fishersci.setPageName();
            window.digitalData.page.url = document.location.href;
        },
        /**
         * getCurrency function facilitates getting currency code and symbol based on locale
         * Returns an object with currencyCode and currencySymbol properties
         */
        getCurrency: function(locale) {
            if (!locale || typeof locale !== 'string') {
                return { currencyCode: 'EUR', currencySymbol: '€' }; // Default to EUR
            }

            var normalizedLocale = locale.toLowerCase();

            // Currency mapping based on EU locales
            var currencyMap = {
                // Eurozone countries
                'en_at': { currencyCode: 'EUR', currencySymbol: '€' },
                'de_at': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_be': { currencyCode: 'EUR', currencySymbol: '€' },
                'fr_be': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_fi': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_fr': { currencyCode: 'EUR', currencySymbol: '€' },
                'fr_fr': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_de': { currencyCode: 'EUR', currencySymbol: '€' },
                'de_de': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_ie': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_it': { currencyCode: 'EUR', currencySymbol: '€' },
                'it_it': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_nl': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_pt': { currencyCode: 'EUR', currencySymbol: '€' },
                'en_es': { currencyCode: 'EUR', currencySymbol: '€' },
                'es_es': { currencyCode: 'EUR', currencySymbol: '€' },

                // Switzerland
                'en_ch': { currencyCode: 'CHF', currencySymbol: 'CHF' },
                'it_ch': { currencyCode: 'CHF', currencySymbol: 'CHF' },
                'de_ch': { currencyCode: 'CHF', currencySymbol: 'CHF' },
                'fr_ch': { currencyCode: 'CHF', currencySymbol: 'CHF' },

                // Denmark
                'da_dk': { currencyCode: 'DKK', currencySymbol: 'kr' },
                'en_dk': { currencyCode: 'DKK', currencySymbol: 'kr' },

                // United Kingdom
                'en_gb': { currencyCode: 'GBP', currencySymbol: '£' },

                // Norway
                'en_no': { currencyCode: 'NOK', currencySymbol: 'kr' },

                // Sweden
                'en_se': { currencyCode: 'SEK', currencySymbol: 'kr' },
                'sv_se': { currencyCode: 'SEK', currencySymbol: 'kr' }
            };

            return currencyMap[normalizedLocale] || { currencyCode: 'EUR', currencySymbol: '€' };
        },

        /** Mapping of eStore cookie values to display names. */
        eStores: {
            "estore-scientific": "Fisher Scientific",
            "estore-healthcare": "Fisher Healthcare",
            "estore-scied": "Fisher Science Education"
        },

        /**
         * Derive a normalized page name from the current URL.
         * - "home" => "home"
         * - Search results => "search results"
         * - 404/sorry pages => ""
         * - Otherwise: path lowercased, "/" replaced with ":" (e.g., ":store:cart")
         * @returns {string} Derived page name (may be empty).
         */
        setPageName: function () {
            var pathname = document.location.pathname;
            if (pathname.indexOf("home") > -1) {
                return "home";
            } else if (pathname.indexOf("search/products") > -1) {
                return "search results";
            } else if (document.querySelector("h1") && document.querySelector("h1").innerText.toLowerCase().indexOf("sorry") > -1) {
                return "";
            } else {
                var pagename = pathname.toLowerCase().replaceAll(/\//g, ":");
                return pagename;
            }
        },

        /**
         * Derive a semantic page type using pattern matching against the pathname.
         * Falls back to "Other" when no pattern matches.
         * @returns {string} Page type label.
         */
        setPageType: function () {
            var pageType = document.title;
            var pathname = document.location.pathname;
            var typePatternMatching = {
                "Account": new RegExp("(account-dashboard|/users/profiles/)", "g"),
                "Brands": new RegExp("(/browse/brands)", "g"),
                "Brands:Browse by Brand": new RegExp("(/brands/)", "g"),
                "Browse by Product": new RegExp("/browse/[0-9].*/[A-z].*", "g"),
                "Cart": new RegExp("(/store1/cart|/shop/orderdisplayview|/cart/[0-9].*/update)", "g"),
                "Customer Support": new RegExp("(customer-help-support|contactus)", "g"),
                "Homepage": new RegExp("(home|healthcare-products|education-products)\\.html", "g"),
                "My List": new RegExp("(/store1/mylist)", "g"),
                "Orders:Detail": new RegExp("(/store1/orders/details/)", "g"),
                "Orders": new RegExp("(/store1/orders?)", "g"),
                "Quick Order": new RegExp("(/store1/rapidorder|/shop/CartAdd.html)", "g"),
                "Quotes:New": new RegExp("/store1/quotes/newquote", "g"),
                "Quotes:Detail": new RegExp("(/store1/quotes/[A-z0-9])", "g"),
                "Quotes": new RegExp("(/store1/quotes)", "g"),
                "Registration": new RegExp("/users/(userregistration|account-activation)", "g"),
                "Returns": new RegExp("/returns/", "g"),
                "Search Results:Rapid": new RegExp("/catalog/search/products", "g"),
                "Search:Certificates": new RegExp("/catalog/search/certificates.html", "g"),
                "Search Results:Certificates": new RegExp("/catalog/search/certificateResults", "g"),
                "Search:SDS": new RegExp("/catalog/search/sdshome.html", "g"),
                "Search Results:SDS": new RegExp("/catalog/search/sds", "g"),
                "Subscription Orders: Manage": new RegExp("/subscriptionorders/manage", "g"),
                "Subscription Order: Details": new RegExp("/subscriptionorders/orderdetails", "g"),
                "Webinars": new RegExp("/webinars.html", "g"),
                "Webinar": new RegExp("/webinars/[A-z].*\\.html", "g")
            };

            for (var type in typePatternMatching) {
                if (typePatternMatching.hasOwnProperty(type) && typePatternMatching[type].test(pathname)) {
                    return type;
                }
            }
            return "Other";
        },

        /**
         * Populate additional page metadata from <meta> tags when available.
         * - template: meta[name="template"]
         * - aem: meta[name="aem"]
         */
        setPageMeta: function () {
            if (document.querySelector("meta[name='template']")) {
                window.digitalData.page.template = document.querySelector("meta[name='template']").content;
            }
            if (document.querySelector("meta[name='aem']")) {
                window.digitalData.page.aem = document.querySelector("meta[name='aem']").content;
            }
        },

        /**
         * Resolve the current eStore label via cookie lookup.
         * Cookie: "estore" -> maps via `eStores`.
         */
        setStore: function () {
            if (window.digitalDataManager.utilities.getCookie("estore")) {
                window.digitalData.general.eStore =
                    digitalDataManager.fishersci.eStores[window.digitalDataManager.utilities.getCookie("estore")];
            }
        },

        /**
         * Normalize and set user details in `digitalData.user`.
         * Accepts payload from the account dropdown/user endpoint and maps it
         * into a consistent profile structure.
         * Also derives:
         *  - registrationType from cookie "userTypeDescription" (if present)
         *  - type: "B2B" when `punchout` truthy, otherwise "B2C"
         * @param {Object} user - Raw user object from application session.
         */
        setUser: function (user) {
            var profile = {};

            var profileStructure = {
                firstName: "firstName",
                lastName: "lastName",
                userEmail: "email",
                userIsPunchOut: "punchout",
                memberId: "id",
                logonId: "username",
                superUser: "superUser",
                accountNumber: "additional.accountNumber",
                accountAlias: "additional.accountAlias",
                company: "additional.company",
                priceAgreementNbrs: "pricingAgreement.number",
                pricingAccountGroup: "additional.pricingAccountGroup",
                obfuscatedKey: "pricingAgreement.key"
            };

            for (var key in profileStructure) {
                if (Object.prototype.hasOwnProperty.call(profileStructure, key) && user[key]) {
                    if (profileStructure[key].indexOf(".") > -1) {
                        var path = profileStructure[key].split(".");
                        if (!(path[0] in profile)) {
                            profile[path[0]] = {};
                        }
                        profile[path[0]][path[1]] = user[key];
                    } else {
                        profile[profileStructure[key]] = user[key];
                    }
                }
            }

            if (window.digitalDataManager.utilities.getCookie("userTypeDescription")) {
                profile.registrationType = window.digitalDataManager.utilities.getCookie("userTypeDescription");
            }

            profile.type = profile.punchout ? "B2B" : "B2C";

            window.digitalData.user = profile;
        },

        /**
         * Build a breadcrumb text array from schema.org markup on the page.
         * @returns {string[]} Array of breadcrumb item texts (trimmed, normalized spaces).
         */
        setBreadcrumbs: function () {
            var breadcrumbs = document.querySelectorAll("[itemtype$='schema.org/BreadcrumbList'] [itemtype$='schema.org/ListItem']") || [];
            var breadcrumbTexts = [];
            for (var i = 0; i < breadcrumbs.length; i++) {
                var t = (breadcrumbs[i].innerText || breadcrumbs[i].textContent || "").replace(/\s+/g, " ");
                breadcrumbTexts.push(t);
            }
            return breadcrumbTexts;
        }
    }
}

/**
 * Country lookup table
 * ---------------------------------------------------------------------------
 * Adds country metadata by two/three-letter codes.
 * Accessed via: `digitalDataManager.utilities.countries[iso2]`
 * Each entry:
 * {
 *   name: string,
 *   twoLetterCode: string,
 *   threeLetterCode: string
 * }
 */
window.digitalDataManager.utilities.countries = {
    ad: {
        name: "Andorra",
        twoLetterCode: "ad",
        threeLetterCode: "and"
    },
    ae: {
        name: "United Arab Emirates",
        twoLetterCode: "ae",
        threeLetterCode: "are"
    },
    af: {
        name: "Afghanistan",
        twoLetterCode: "af",
        threeLetterCode: "afg"
    },
    ag: {
        name: "Antigua and Barbuda",
        twoLetterCode: "ag",
        threeLetterCode: "atg"
    },
    al: {
        name: "Albania",
        twoLetterCode: "al",
        threeLetterCode: "alb"
    },
    am: {
        name: "Armenia",
        twoLetterCode: "am",
        threeLetterCode: "arm"
    },
    ao: {
        name: "Angola",
        twoLetterCode: "ao",
        threeLetterCode: "ago"
    },
    aq: {
        name: "Antarctica",
        twoLetterCode: "aq",
        threeLetterCode: "ata"
    },
    ar: {
        name: "Argentina",
        twoLetterCode: "ar",
        threeLetterCode: "arg"
    },
    at: {
        name: "Austria",
        twoLetterCode: "at",
        threeLetterCode: "aut"
    },
    au: {
        name: "Australia",
        twoLetterCode: "au",
        threeLetterCode: "aus"
    },
    aw: {
        name: "Aruba",
        twoLetterCode: "aw",
        threeLetterCode: "abw"
    },
    az: {
        name: "Azerbaijan",
        twoLetterCode: "az",
        threeLetterCode: "aze"
    },
    ba: {
        name: "Bosnia and Herzegovina",
        twoLetterCode: "ba",
        threeLetterCode: "bih"
    },
    bb: {
        name: "Barbados",
        twoLetterCode: "bb",
        threeLetterCode: "brb"
    },
    bd: {
        name: "Bangladesh",
        twoLetterCode: "bd",
        threeLetterCode: "bgd"
    },
    be: {
        name: "Belgium",
        twoLetterCode: "be",
        threeLetterCode: "bel"
    },
    bf: {
        name: "Burkina Faso",
        twoLetterCode: "bf",
        threeLetterCode: "bfa"
    },
    bg: {
        name: "Bulgaria",
        twoLetterCode: "bg",
        threeLetterCode: "bgr"
    },
    bh: {
        name: "Bahrain",
        twoLetterCode: "bh",
        threeLetterCode: "bhr"
    },
    bi: {
        name: "Burundi",
        twoLetterCode: "bi",
        threeLetterCode: "bdi"
    },
    bj: {
        name: "Benin",
        twoLetterCode: "bj",
        threeLetterCode: "ben"
    },
    bm: {
        name: "Bermuda",
        twoLetterCode: "bm",
        threeLetterCode: "bmu"
    },
    bn: {
        name: "Brunei Darussalam",
        twoLetterCode: "bn",
        threeLetterCode: "brn"
    },
    bo: {
        name: "Bolivia",
        twoLetterCode: "bo",
        threeLetterCode: "bol"
    },
    br: {
        name: "Brazil",
        twoLetterCode: "br",
        threeLetterCode: "bra"
    },
    bs: {
        name: "Bahamas",
        twoLetterCode: "bs",
        threeLetterCode: "bhs"
    },
    bt: {
        name: "Bhutan",
        twoLetterCode: "bt",
        threeLetterCode: "btn"
    },
    bv: {
        name: "Bouvet Island",
        twoLetterCode: "bv",
        threeLetterCode: "bvt"
    },
    bw: {
        name: "Botswana",
        twoLetterCode: "bw",
        threeLetterCode: "bwa"
    },
    by: {
        name: "Belarus",
        twoLetterCode: "by",
        threeLetterCode: "blr"
    },
    bz: {
        name: "Belize",
        twoLetterCode: "bz",
        threeLetterCode: "blz"
    },
    ca: {
        name: "Canada",
        twoLetterCode: "ca",
        threeLetterCode: "can"
    },
    cd: {
        name: "Democratic Republic of the Congo",
        twoLetterCode: "cd",
        threeLetterCode: "cod"
    },
    cf: {
        name: "Central African Republic",
        twoLetterCode: "cf",
        threeLetterCode: "caf"
    },
    cg: {
        name: "Congo",
        twoLetterCode: "cg",
        threeLetterCode: "cog"
    },
    ch: {
        name: "Switzerland",
        twoLetterCode: "ch",
        threeLetterCode: "che"
    },
    ci: {
        name: "Cote d'Ivoire",
        twoLetterCode: "ci",
        threeLetterCode: "civ"
    },
    cl: {
        name: "Chile",
        twoLetterCode: "cl",
        threeLetterCode: "chl"
    },
    cm: {
        name: "Cameroon",
        twoLetterCode: "cm",
        threeLetterCode: "cmr"
    },
    cn: {
        name: "China",
        twoLetterCode: "cn",
        threeLetterCode: "chn"
    },
    co: {
        name: "Colombia",
        twoLetterCode: "co",
        threeLetterCode: "col"
    },
    cr: {
        name: "Costa Rica",
        twoLetterCode: "cr",
        threeLetterCode: "cri"
    },
    cv: {
        name: "Cape Verde",
        twoLetterCode: "cv",
        threeLetterCode: "cpv"
    },
    cw: {
        name: "Curacao",
        twoLetterCode: "cw",
        threeLetterCode: "cuw"
    },
    cy: {
        name: "Cyprus",
        twoLetterCode: "cy",
        threeLetterCode: "cyp"
    },
    cz: {
        name: "Czech Republic",
        twoLetterCode: "cz",
        threeLetterCode: "cze"
    },
    de: {
        name: "Germany",
        twoLetterCode: "de",
        threeLetterCode: "deu"
    },
    dj: {
        name: "Djibouti",
        twoLetterCode: "dj",
        threeLetterCode: "dji"
    },
    dk: {
        name: "Denmark",
        twoLetterCode: "dk",
        threeLetterCode: "dnk"
    },
    dm: {
        name: "Dominica",
        twoLetterCode: "dm",
        threeLetterCode: "dma"
    },
    do: {
        name: "Dominican Republic",
        twoLetterCode: "do",
        threeLetterCode: "dom"
    },
    dz: {
        name: "Algeria",
        twoLetterCode: "dz",
        threeLetterCode: "dza"
    },
    ec: {
        name: "Ecuador",
        twoLetterCode: "ec",
        threeLetterCode: "ecu"
    },
    ee: {
        name: "Estonia",
        twoLetterCode: "ee",
        threeLetterCode: "est"
    },
    eg: {
        name: "Egypt",
        twoLetterCode: "eg",
        threeLetterCode: "egy"
    },
    eh: {
        name: "Western Sahara",
        twoLetterCode: "eh",
        threeLetterCode: "esh"
    },
    er: {
        name: "Eritrea",
        twoLetterCode: "er",
        threeLetterCode: "eri"
    },
    es: {
        name: "Spain",
        twoLetterCode: "es",
        threeLetterCode: "esp"
    },
    et: {
        name: "Ethiopia",
        twoLetterCode: "et",
        threeLetterCode: "eth"
    },
    fi: {
        name: "Finland",
        twoLetterCode: "fi",
        threeLetterCode: "fin"
    },
    fj: {
        name: "Fiji",
        twoLetterCode: "fj",
        threeLetterCode: "fji"
    },
    fo: {
        name: "Faroe Islands",
        twoLetterCode: "fo",
        threeLetterCode: "fro"
    },
    fr: {
        name: "France",
        twoLetterCode: "fr",
        threeLetterCode: "fra"
    },
    fx: {
        name: "France Metropolitan",
        twoLetterCode: "fx",
        threeLetterCode: "fxx"
    },
    ga: {
        name: "Gabon",
        twoLetterCode: "ga",
        threeLetterCode: "gab"
    },
    gd: {
        name: "Grenada",
        twoLetterCode: "gd",
        threeLetterCode: "grd"
    },
    ge: {
        name: "Georgia",
        twoLetterCode: "ge",
        threeLetterCode: "geo"
    },
    gf: {
        name: "French Guiana",
        twoLetterCode: "gf",
        threeLetterCode: "guf"
    },
    gh: {
        name: "Ghana",
        twoLetterCode: "gh",
        threeLetterCode: "gha"
    },
    gi: {
        name: "Gibraltar",
        twoLetterCode: "gi",
        threeLetterCode: "gib"
    },
    gl: {
        name: "Greenland",
        twoLetterCode: "gl",
        threeLetterCode: "grl"
    },
    gm: {
        name: "Gambia",
        twoLetterCode: "gm",
        threeLetterCode: "gmb"
    },
    gn: {
        name: "Guinea",
        twoLetterCode: "gn",
        threeLetterCode: "gin"
    },
    gp: {
        name: "Guadeloupe",
        twoLetterCode: "gp",
        threeLetterCode: "glp"
    },
    gr: {
        name: "Greece",
        twoLetterCode: "gr",
        threeLetterCode: "grc"
    },
    gs: {
        name: "South Georgia and the South Sandwich Islands",
        twoLetterCode: "gs",
        threeLetterCode: "sgs"
    },
    gt: {
        name: "Guatemala",
        twoLetterCode: "gt",
        threeLetterCode: "gtm"
    },
    gu: {
        name: "Guam",
        twoLetterCode: "gu",
        threeLetterCode: "gum"
    },
    gy: {
        name: "Guyana",
        twoLetterCode: "gy",
        threeLetterCode: "guy"
    },
    hk: {
        name: "Hong Kong",
        twoLetterCode: "hk",
        threeLetterCode: "hkg"
    },
    hn: {
        name: "Honduras",
        twoLetterCode: "hn",
        threeLetterCode: "hnd"
    },
    hr: {
        name: "Croatia",
        twoLetterCode: "hr",
        threeLetterCode: "hrv"
    },
    ht: {
        name: "Haiti",
        twoLetterCode: "ht",
        threeLetterCode: "hti"
    },
    hu: {
        name: "Hungary",
        twoLetterCode: "hu",
        threeLetterCode: "hun"
    },
    ic: {
        name: "Canary Islands",
        twoLetterCode: "ic",
        threeLetterCode: "ic"
    },
    id: {
        name: "Indonesia",
        twoLetterCode: "id",
        threeLetterCode: "idn"
    },
    ie: {
        name: "Ireland",
        twoLetterCode: "ie",
        threeLetterCode: "irl"
    },
    il: {
        name: "Israel",
        twoLetterCode: "il",
        threeLetterCode: "isr"
    },
    in: {
        name: "India",
        twoLetterCode: "in",
        threeLetterCode: "ind"
    },
    iq: {
        name: "Iraq",
        twoLetterCode: "iq",
        threeLetterCode: "irq"
    },
    is: {
        name: "Iceland",
        twoLetterCode: "is",
        threeLetterCode: "isl"
    },
    it: {
        name: "Italy",
        twoLetterCode: "it",
        threeLetterCode: "ita"
    },
    jm: {
        name: "Jamaica",
        twoLetterCode: "jm",
        threeLetterCode: "jam"
    },
    jo: {
        name: "Jordan",
        twoLetterCode: "jo",
        threeLetterCode: "jor"
    },
    jp: {
        name: "Japan",
        twoLetterCode: "jp",
        threeLetterCode: "jpn"
    },
    ke: {
        name: "Kenya",
        twoLetterCode: "ke",
        threeLetterCode: "ken"
    },
    kg: {
        name: "Kyrgyzstan",
        twoLetterCode: "kg",
        threeLetterCode: "kgz"
    },
    kh: {
        name: "Cambodia",
        twoLetterCode: "kh",
        threeLetterCode: "khm"
    },
    km: {
        name: "Comoros",
        twoLetterCode: "km",
        threeLetterCode: "com"
    },
    kn: {
        name: "Saint Kitts and Nevis",
        twoLetterCode: "kn",
        threeLetterCode: "kna"
    },
    kr: {
        name: "Korea, Republic of",
        twoLetterCode: "kr",
        threeLetterCode: "kor"
    },
    kw: {
        name: "Kuwait",
        twoLetterCode: "kw",
        threeLetterCode: "kwt"
    },
    ky: {
        name: "Cayman Islands",
        twoLetterCode: "ky",
        threeLetterCode: "cym"
    },
    kz: {
        name: "Kazakhstan",
        twoLetterCode: "kz",
        threeLetterCode: "kaz"
    },
    la: {
        name: "Lao People's Democratic Republ",
        twoLetterCode: "la",
        threeLetterCode: "lao"
    },
    lb: {
        name: "Lebanon",
        twoLetterCode: "lb",
        threeLetterCode: "lbn"
    },
    lc: {
        name: "Saint Lucia",
        twoLetterCode: "lc",
        threeLetterCode: "lca"
    },
    li: {
        name: "Liechtenstein",
        twoLetterCode: "li",
        threeLetterCode: "lie"
    },
    lk: {
        name: "Sri Lanka",
        twoLetterCode: "lk",
        threeLetterCode: "lka"
    },
    lr: {
        name: "Liberia",
        twoLetterCode: "lr",
        threeLetterCode: "lbr"
    },
    ls: {
        name: "Lesotho",
        twoLetterCode: "ls",
        threeLetterCode: "lso"
    },
    lt: {
        name: "Lithuania",
        twoLetterCode: "lt",
        threeLetterCode: "ltu"
    },
    lu: {
        name: "Luxembourg",
        twoLetterCode: "lu",
        threeLetterCode: "lux"
    },
    lv: {
        name: "Latvia",
        twoLetterCode: "lv",
        threeLetterCode: "lva"
    },
    ly: {
        name: "Libyan Arab Jamahiriya",
        twoLetterCode: "ly",
        threeLetterCode: "lby"
    },
    ma: {
        name: "Morocco",
        twoLetterCode: "ma",
        threeLetterCode: "mar"
    },
    mc: {
        name: "Monaco",
        twoLetterCode: "mc",
        threeLetterCode: "mco"
    },
    md: {
        name: "Moldova, Republic of",
        twoLetterCode: "md",
        threeLetterCode: "mda"
    },
    me: {
        name: "Montenegro",
        twoLetterCode: "me",
        threeLetterCode: "mne"
    },
    mg: {
        name: "Madagascar",
        twoLetterCode: "mg",
        threeLetterCode: "mdg"
    },
    mk: {
        name: "Macedonia",
        twoLetterCode: "mk",
        threeLetterCode: "mkd"
    },
    ml: {
        name: "Mali",
        twoLetterCode: "ml",
        threeLetterCode: "mli"
    },
    mm: {
        name: "Myanmar",
        twoLetterCode: "mm",
        threeLetterCode: "mmr"
    },
    mn: {
        name: "Mongolia",
        twoLetterCode: "mn",
        threeLetterCode: "mng"
    },
    mo: {
        name: "Macao",
        twoLetterCode: "mo",
        threeLetterCode: "mac"
    },
    mq: {
        name: "Martinique",
        twoLetterCode: "mq",
        threeLetterCode: "mtq"
    },
    mr: {
        name: "Mauritania",
        twoLetterCode: "mr",
        threeLetterCode: "mrt"
    },
    ms: {
        name: "Montserrat",
        twoLetterCode: "ms",
        threeLetterCode: "msr"
    },
    mt: {
        name: "Malta",
        twoLetterCode: "mt",
        threeLetterCode: "mlt"
    },
    mu: {
        name: "Mauritius",
        twoLetterCode: "mu",
        threeLetterCode: "mus"
    },
    mv: {
        name: "Maldives",
        twoLetterCode: "mv",
        threeLetterCode: "mdv"
    },
    mw: {
        name: "Malawi",
        twoLetterCode: "mw",
        threeLetterCode: "mwi"
    },
    mx: {
        name: "Mexico",
        twoLetterCode: "mx",
        threeLetterCode: "mex"
    },
    my: {
        name: "Malaysia",
        twoLetterCode: "my",
        threeLetterCode: "mys"
    },
    mz: {
        name: "Mozambique",
        twoLetterCode: "mz",
        threeLetterCode: "moz"
    },
    na: {
        name: "Namibia",
        twoLetterCode: "na",
        threeLetterCode: "nam"
    },
    nc: {
        name: "New Caledonia",
        twoLetterCode: "nc",
        threeLetterCode: "ncl"
    },
    ne: {
        name: "Niger",
        twoLetterCode: "ne",
        threeLetterCode: "ner"
    },
    ng: {
        name: "Nigeria",
        twoLetterCode: "ng",
        threeLetterCode: "nga"
    },
    ni: {
        name: "Nicaragua",
        twoLetterCode: "ni",
        threeLetterCode: "nic"
    },
    nl: {
        name: "Netherlands",
        twoLetterCode: "nl",
        threeLetterCode: "nld"
    },
    no: {
        name: "Norway",
        twoLetterCode: "no",
        threeLetterCode: "nor"
    },
    np: {
        name: "Nepal",
        twoLetterCode: "np",
        threeLetterCode: "npl"
    },
    nz: {
        name: "New Zealand",
        twoLetterCode: "nz",
        threeLetterCode: "nzl"
    },
    om: {
        name: "Oman",
        twoLetterCode: "om",
        threeLetterCode: "omn"
    },
    pa: {
        name: "Panama",
        twoLetterCode: "pa",
        threeLetterCode: "pan"
    },
    pe: {
        name: "Peru",
        twoLetterCode: "pe",
        threeLetterCode: "per"
    },
    pf: {
        name: "French Polynesia",
        twoLetterCode: "pf",
        threeLetterCode: "pyf"
    },
    pg: {
        name: "Papua New Guinea",
        twoLetterCode: "pg",
        threeLetterCode: "png"
    },
    ph: {
        name: "Philippines",
        twoLetterCode: "ph",
        threeLetterCode: "phl"
    },
    pk: {
        name: "Pakistan",
        twoLetterCode: "pk",
        threeLetterCode: "pak"
    },
    pl: {
        name: "Poland",
        twoLetterCode: "pl",
        threeLetterCode: "pol"
    },
    pr: {
        name: "Puerto Rico",
        twoLetterCode: "pr",
        threeLetterCode: "pri"
    },
    ps: {
        name: "Palestine, State of",
        twoLetterCode: "ps",
        threeLetterCode: "pse"
    },
    pt: {
        name: "Portugal",
        twoLetterCode: "pt",
        threeLetterCode: "prt"
    },
    py: {
        name: "Paraguay",
        twoLetterCode: "py",
        threeLetterCode: "pry"
    },
    qa: {
        name: "Qatar",
        twoLetterCode: "qa",
        threeLetterCode: "qat"
    },
    re: {
        name: "Reunion",
        twoLetterCode: "re",
        threeLetterCode: "reu"
    },
    ro: {
        name: "Romania",
        twoLetterCode: "ro",
        threeLetterCode: "rou"
    },
    rs: {
        name: "Serbia",
        twoLetterCode: "rs",
        threeLetterCode: "srb"
    },
    ru: {
        name: "Russian Federation",
        twoLetterCode: "ru",
        threeLetterCode: "rus"
    },
    rw: {
        name: "Rwanda",
        twoLetterCode: "rw",
        threeLetterCode: "rwa"
    },
    sa: {
        name: "Saudi Arabia",
        twoLetterCode: "sa",
        threeLetterCode: "sau"
    },
    sc: {
        name: "Seychelles",
        twoLetterCode: "sc",
        threeLetterCode: "syc"
    },
    se: {
        name: "Sweden",
        twoLetterCode: "se",
        threeLetterCode: "swe"
    },
    sg: {
        name: "Singapore",
        twoLetterCode: "sg",
        threeLetterCode: "sgp"
    },
    sh: {
        name: "Saint Helena",
        twoLetterCode: "sh",
        threeLetterCode: "shn"
    },
    si: {
        name: "Slovenia",
        twoLetterCode: "si",
        threeLetterCode: "svn"
    },
    sj: {
        name: "Svalbard and Jan Mayen",
        twoLetterCode: "sj",
        threeLetterCode: "sjm"
    },
    sk: {
        name: "Slovakia (Slovak Republic)",
        twoLetterCode: "sk",
        threeLetterCode: "svk"
    },
    sl: {
        name: "Sierra Leone",
        twoLetterCode: "sl",
        threeLetterCode: "sle"
    },
    sm: {
        name: "San Marino",
        twoLetterCode: "sm",
        threeLetterCode: "smr"
    },
    sn: {
        name: "Senegal",
        twoLetterCode: "sn",
        threeLetterCode: "sen"
    },
    so: {
        name: "Somalia",
        twoLetterCode: "so",
        threeLetterCode: "som"
    },
    sr: {
        name: "Suriname",
        twoLetterCode: "sr",
        threeLetterCode: "sur"
    },
    st: {
        name: "Sao Tome and Principe",
        twoLetterCode: "st",
        threeLetterCode: "stp"
    },
    sv: {
        name: "El Salvador",
        twoLetterCode: "sv",
        threeLetterCode: "slv"
    },
    sy: {
        name: "Syrian Arab Republic",
        twoLetterCode: "sy",
        threeLetterCode: "syr"
    },
    sz: {
        name: "Swaziland",
        twoLetterCode: "sz",
        threeLetterCode: "swz"
    },
    tc: {
        name: "Turks and Caicos Islands",
        twoLetterCode: "tc",
        threeLetterCode: "tca"
    },
    td: {
        name: "Chad",
        twoLetterCode: "td",
        threeLetterCode: "tcd"
    },
    tg: {
        name: "Togo",
        twoLetterCode: "tg",
        threeLetterCode: "tgo"
    },
    th: {
        name: "Thailand",
        twoLetterCode: "th",
        threeLetterCode: "tha"
    },
    tj: {
        name: "Tajikistan",
        twoLetterCode: "tj",
        threeLetterCode: "tjk"
    },
    tm: {
        name: "Turkmenistan",
        twoLetterCode: "tm",
        threeLetterCode: "tkm"
    },
    tn: {
        name: "Tunisia",
        twoLetterCode: "tn",
        threeLetterCode: "tun"
    },
    to: {
        name: "Tonga",
        twoLetterCode: "to",
        threeLetterCode: "ton"
    },
    tr: {
        name: "Turkey",
        twoLetterCode: "tr",
        threeLetterCode: "tur"
    },
    tt: {
        name: "Trinidad and Tobago",
        twoLetterCode: "tt",
        threeLetterCode: "tto"
    },
    tw: {
        name: "Taiwan",
        twoLetterCode: "tw",
        threeLetterCode: "twn"
    },
    tz: {
        name: "United Republic of Tanzania",
        twoLetterCode: "tz",
        threeLetterCode: "tza"
    },
    ua: {
        name: "Ukraine",
        twoLetterCode: "ua",
        threeLetterCode: "ukr"
    },
    ug: {
        name: "Uganda",
        twoLetterCode: "ug",
        threeLetterCode: "uga"
    },
    gb: {
        name: "United Kingdom",
        twoLetterCode: "gb",
        threeLetterCode: "gbr"
    },
    um: {
        name: "United States Minor Outlying Islands",
        twoLetterCode: "um",
        threeLetterCode: "umi"
    },
    us: {
        name: "United States",
        twoLetterCode: "us",
        threeLetterCode: "usa"
    },
    uy: {
        name: "Uruguay",
        twoLetterCode: "uy",
        threeLetterCode: "ury"
    },
    uz: {
        name: "Uzbekistan",
        twoLetterCode: "uz",
        threeLetterCode: "uzb"
    },
    va: {
        name: "Holy See (Vatican City State)",
        twoLetterCode: "va",
        threeLetterCode: "vat"
    },
    vc: {
        name: "Saint Vincent and the Grenadines",
        twoLetterCode: "vc",
        threeLetterCode: "vct"
    },
    ve: {
        name: "Venezuela",
        twoLetterCode: "ve",
        threeLetterCode: "ven"
    },
    vg: {
        name: "British Virgin Islands",
        twoLetterCode: "vg",
        threeLetterCode: "vgb"
    },
    vi: {
        name: "US Virgin Islands",
        twoLetterCode: "vi",
        threeLetterCode: "vir"
    },
    vn: {
        name: "Viet Nam",
        twoLetterCode: "vn",
        threeLetterCode: "vnm"
    },
    ye: {
        name: "Yemen",
        twoLetterCode: "ye",
        threeLetterCode: "yem"
    },
    yt: {
        name: "Mayotte",
        twoLetterCode: "yt",
        threeLetterCode: "myt"
    },
    za: {
        name: "South Africa",
        twoLetterCode: "za",
        threeLetterCode: "zaf"
    },
    zm: {
        name: "Zambia",
        twoLetterCode: "zm",
        threeLetterCode: "zmb"
    },
    zw: {
        name: "Zimbabwe",
        twoLetterCode: "zw",
        threeLetterCode: "zwe"
    },
}

/**
 * Bootstrap: initialize the data layer from locale cookie.
 * - Cookie "locale" expected in the format "lang_ISO" (e.g., "en_US").
 * - Currency should normally be provided by the commerce backend; here we pass USD/$ for example.
 */
var isoLang = window.digitalDataManager.utilities.getCookie("locale").toLowerCase().split("_");
// Get the appropriate currency based on the locale
var currency = window.digitalDataManager.fishersci.getCurrency(window.digitalDataManager.utilities.getCookie("locale"));
// Initialize with proper currency for the locale
window.digitalDataManager.fishersci.init(isoLang[1], isoLang[0], currency.currencyCode, currency.currencySymbol);